import { useEffect, useState } from 'react';

/**
 * The smallest router that gives a screen a URL.
 *
 * Focus mode wanted to be a link — "go translate Dutch" is something worth
 * sending someone — and the batch wanted a place to come back to from it.
 * Two paths do not justify a dependency, so this is pushState and popstate
 * and nothing else. Everything unmatched is the workspace.
 */
export interface Route {
  path: string;
  params: URLSearchParams;
}

const read = (): Route => ({
  path: window.location.pathname.replace(/\/+$/, '') || '/',
  params: new URLSearchParams(window.location.search),
});

const listeners = new Set<() => void>();

export function navigate(to: string, opts: { replace?: boolean } = {}) {
  const current = window.location.pathname + window.location.search;
  if (to === current) return;
  if (opts.replace) window.history.replaceState(null, '', to);
  else window.history.pushState(null, '', to);
  for (const l of listeners) l();
}

export function useRoute(): Route {
  const [route, setRoute] = useState(read);
  useEffect(() => {
    const update = () => setRoute(read());
    listeners.add(update);
    window.addEventListener('popstate', update);
    return () => {
      listeners.delete(update);
      window.removeEventListener('popstate', update);
    };
  }, []);
  return route;
}
